import { useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

const _targetOffset = new THREE.Vector3();
const _dir = new THREE.Vector3();

const CameraController = ({ targetPosition, isAnimating, onArrived, controlsRef }) => {
  const { camera } = useThree();
  const arrivedRef = useRef(false);

  useFrame(() => {
    if (!isAnimating || !targetPosition) {
      arrivedRef.current = false;
      return;
    }

    const planetVec = new THREE.Vector3(...targetPosition);
    _dir.copy(planetVec).normalize();
    const distFromOrigin = planetVec.length();

    // Approach from the sun-opposite side, always ~24 units past the planet.
    // This prevents the camera from flying through the sun for inner planets
    // (old formula broke for Mercury r=16) and keeps a consistent close-up
    // distance regardless of orbit radius.
    if (distFromOrigin < 1) {
      // The Sun — come in from a fixed above-front position
      _targetOffset.set(0, 16, 38);
    } else {
      // Approach from the planet's outward side, 16 units away.
      // At this distance the planet fills ~40 % of the screen and dominates.
      _targetOffset.copy(planetVec).addScaledVector(_dir, 16);
      _targetOffset.y += 6;
    }

    // Lerp camera toward the approach position
    camera.position.lerp(_targetOffset, 0.04);

    // Smoothly look toward the planet
    const lookTarget = planetVec.clone();
    const currentLookAt = new THREE.Vector3();
    camera.getWorldDirection(currentLookAt);
    const desiredDir = lookTarget.sub(camera.position).normalize();
    currentLookAt.lerp(desiredDir, 0.05);
    camera.lookAt(camera.position.clone().add(currentLookAt));

    // Trigger arrival when close enough
    const distToTarget = camera.position.distanceTo(_targetOffset);
    if (distToTarget < 4 && !arrivedRef.current) {
      arrivedRef.current = true;
      // Set OrbitControls target to the planet so user orbits around it
      if (controlsRef?.current) {
        const pv = new THREE.Vector3(...targetPosition);
        controlsRef.current.target.copy(pv);
      }
      onArrived();
    }
  });

  return null;
};

export default CameraController;
